import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  fetchPublicProfile,
  fetchPublicAvailableSlots,
  fetchPublicHighlightedDays,
  createPublicReservation,
  CreateReservationPayload,
  TimeSlot,
  DayAvailability,
} from './api';
import { PublicProfileData } from './types';

export const publicProfileQueryKey = (userId: string) => ['publicProfile', userId];
export const publicSlotsQueryKey = (userId: string, date: string, serviceId: string) => [
  'publicSlots',
  userId,
  date,
  serviceId,
];
export const publicHighlightedDaysQueryKey = (userId: string, month: string, serviceId: string) => [
  'publicHighlightedDays',
  userId,
  month,
  serviceId,
];

export const usePublicProfileQuery = (userId: string | undefined) =>
  useQuery<PublicProfileData>({
    queryKey: publicProfileQueryKey(userId ?? ''),
    queryFn: () => fetchPublicProfile(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

export const usePublicAvailableSlotsQuery = (
  userId: string | undefined,
  date: string | undefined,
  serviceId: string | undefined,
) =>
  useQuery<{ slots: TimeSlot[]; reason?: string }>({
    queryKey: publicSlotsQueryKey(userId ?? '', date ?? '', serviceId ?? ''),
    queryFn: () => fetchPublicAvailableSlots(userId!, date!, serviceId!),
    enabled: !!(userId && date && serviceId),
    staleTime: 60 * 1000,
  });

export const usePublicHighlightedDaysQuery = (
  userId: string | undefined,
  month: string | undefined,
  serviceId: string | undefined,
) =>
  useQuery<{ days: Record<string, DayAvailability> }>({
    queryKey: publicHighlightedDaysQueryKey(userId ?? '', month ?? '', serviceId ?? ''),
    queryFn: () => fetchPublicHighlightedDays(userId!, month!, serviceId!),
    enabled: !!(userId && month && serviceId),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

export const useCreatePublicReservationMutation = (userId: string | undefined) =>
  useMutation<{ id: string }, Error, CreateReservationPayload>({
    mutationFn: payload => createPublicReservation(userId!, payload),
  });
